import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function PainterJoinPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [attaching, setAttaching] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/referrals/${token}`);
        setRef(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "This invitation is no longer valid.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const attachAndContinue = async () => {
    setAttaching(true);
    try {
      await api.post(`/public/referrals/${token}/attach`);
      nav("/painter/membership/apply");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to attach invitation.");
    } finally { setAttaching(false); }
  };

  const decline = async () => {
    if (!window.confirm("Decline this invitation? Your customer won't be notified.")) return;
    try { await api.post(`/public/referrals/${token}/decline`); } catch (_) {}
    nav("/");
  };

  if (loading) return <div className="p-10 text-center text-brand-navy" data-testid="join-loading">Loading invitation…</div>;
  if (err || !ref) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6 bg-brand-cream" data-testid="join-invalid">
        <div className="max-w-md text-center bg-white border border-brand-border rounded-2xl p-8">
          <h1 className="text-2xl font-bold text-brand-navy mb-2">Invitation not available</h1>
          <p className="text-sm text-gray-700">{err || "This link may have expired or been withdrawn."}</p>
        </div>
      </div>
    );
  }

  const isExpired = ref.status === "expired";
  const isDeclined = ref.status === "declined";
  const inactive = isExpired || isDeclined;

  return (
    <div className="min-h-[80vh] py-10 px-4 sm:px-6 bg-brand-cream" data-testid="painter-join-page">
      <div className="max-w-2xl mx-auto">
        <header className="text-center mb-8">
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold">Personal invitation</div>
          <h1 className="text-3xl sm:text-4xl font-bold text-brand-navy mt-2" data-testid="join-heading">
            You&apos;ve been invited by one of your customers
          </h1>
          <p className="text-base text-gray-700 mt-3">
            <strong className="text-brand-navy" data-testid="join-homeowner-name">{ref.homeowner_display}</strong>
            {" "}has invited you to join ColourMatch Pro so you can quote their painting project.
          </p>
        </header>

        <section className={`bg-white border-2 rounded-2xl p-6 sm:p-8 mb-6 ${inactive ? "border-gray-200 opacity-75" : "border-brand-teal"}`}>
          <div className="text-xs uppercase tracking-widest text-brand-teal font-semibold mb-2">What this means</div>
          <ul className="text-sm text-gray-700 space-y-3">
            <li className="flex gap-3"><span className="text-brand-teal font-bold">✓</span><span>Your customer already trusts you. This is not a marketplace — you&apos;re here because <strong>they asked for you</strong>.</span></li>
            <li className="flex gap-3"><span className="text-brand-teal font-bold">✓</span><span>You&apos;ll see the exact colour scheme your customer built in ColourMatch — no more guessing at shades from screenshots.</span></li>
            <li className="flex gap-3"><span className="text-brand-teal font-bold">✓</span><span><strong>ColourMatch never takes a commission from your jobs.</strong> You keep 100% of every quote you write.</span></li>
            <li className="flex gap-3"><span className="text-brand-teal font-bold">✓</span><span>Because your customer invited you personally, you get <strong data-testid="join-discount-pct">{Math.round(ref.discount_percent)}% off</strong> your first ColourMatch Pro invoice.</span></li>
          </ul>

          {!inactive && (
            <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
              {user ? (
                <button
                  onClick={attachAndContinue}
                  disabled={attaching}
                  className="px-6 py-3 rounded-xl bg-brand-teal text-white font-bold text-sm hover:bg-brand-teal/90 disabled:opacity-50"
                  data-testid="join-continue-btn"
                >
                  {attaching ? "Linking your account…" : "Continue to my application →"}
                </button>
              ) : (
                <>
                  <a
                    href={`/register?next=${encodeURIComponent(`/pro/join/${token}`)}&role=painter&email=${encodeURIComponent(ref.painter_email_hint || "")}`}
                    className="px-6 py-3 rounded-xl bg-brand-teal text-white font-bold text-sm hover:bg-brand-teal/90 text-center"
                    data-testid="join-register-btn"
                  >
                    Create my ColourMatch account
                  </a>
                  <a
                    href={`/login?next=${encodeURIComponent(`/pro/join/${token}`)}`}
                    className="px-6 py-3 rounded-xl border border-brand-navy text-brand-navy font-bold text-sm hover:bg-brand-cream text-center"
                    data-testid="join-login-btn"
                  >
                    I already have an account
                  </a>
                </>
              )}
            </div>
          )}

          {isExpired && (
            <p className="text-sm text-gray-700 mt-4 text-center" data-testid="join-expired">This invitation expired. Ask your customer to send you a new one — it only takes a minute.</p>
          )}
          {isDeclined && (
            <p className="text-sm text-gray-700 mt-4 text-center" data-testid="join-declined">This invitation has been declined.</p>
          )}
        </section>

        {!inactive && (
          <div className="text-center">
            <button onClick={decline} className="text-xs text-gray-500 hover:text-red-700 hover:underline" data-testid="join-decline-btn">
              Not for me — decline this invitation
            </button>
          </div>
        )}

        <p className="text-xs text-center text-gray-500 mt-8">
          Your customer will only see that you joined, not any private details of your application.
        </p>
      </div>
    </div>
  );
}
